import axios from 'axios';
import API_BASE_URL from '../config/api';

axios.defaults.baseURL = axios.defaults.baseURL || API_BASE_URL;

/** Get storage plans (public) */
export const getStoragePlans = async () => {
  try {
    const res = await axios.get('/storage/plans');
    return Array.isArray(res.data) ? res.data : [];
  } catch (error) {
    console.error('Failed to fetch storage plans:', error);
    return [];
  }
};

/** Get dashboard data: storage + video/image counts (requires auth) */
export const getDashboard = async () => {
  try {
    const res = await axios.get('/storage/dashboard');
    return res.data || {};
  } catch (error) {
    console.error('Failed to fetch dashboard:', error);
    return {};
  }
};

/** Get my purchased plans. Pass includeDefault=true to also include free default drive. */
export const getMyPlans = async (includeDefault = false) => {
  const res = await axios.get('/storage/my-plans', {
    params: includeDefault ? { includeDefault: 1 } : undefined,
  });
  return Array.isArray(res.data) ? res.data : [];
};

/** Get current user's transactions (purchase history). Returns { transactions, total }. */
export const getTransactions = async (params = {}) => {
  const res = await axios.get('/storage/transactions', { params: { limit: params.limit || 50, offset: params.offset || 0 } });
  return res.data || { transactions: [], total: 0 };
};

/** Move media between drives. Optional toFolderId = folder on destination drive. */
export const moveMediaBetweenDrives = async (fromUserPlanId, toUserPlanId, mediaIds = null, toFolderId = null) => {
  const body = { fromUserPlanId: String(fromUserPlanId), toUserPlanId: String(toUserPlanId) };
  if (Array.isArray(mediaIds) && mediaIds.length > 0) body.mediaIds = mediaIds;
  if (toFolderId != null && toFolderId !== '') body.toFolderId = toFolderId;
  const res = await axios.post('/storage/move-media', body);
  return res.data;
};

/** Copy media to another drive. Optional toFolderId = folder on destination drive. */
export const copyMediaToDrive = async (fromUserPlanId, toUserPlanId, mediaIds = null, toFolderId = null) => {
  const body = { fromUserPlanId: String(fromUserPlanId), toUserPlanId: String(toUserPlanId) };
  if (Array.isArray(mediaIds) && mediaIds.length > 0) body.mediaIds = mediaIds;
  if (toFolderId != null && toFolderId !== '') body.toFolderId = toFolderId;
  const res = await axios.post('/storage/copy-media', body);
  return res.data;
};

/** Get current user's storage (requires auth) */
export const getUserStorage = async (userId) => {
  try {
    const res = await axios.get('/storage/user');
    const s = res.data;
    if (!s) return { totalStorage: 1, usedStorage: 0, availableStorage: 1 };
    return {
      totalStorage: parseFloat(s.totalStorage) || 0,
      usedStorage: parseFloat(s.usedStorage) || 0,
      availableStorage: parseFloat(s.availableStorage) ?? (parseFloat(s.totalStorage) || 0) - (parseFloat(s.usedStorage) || 0),
    };
  } catch (error) {
    console.error('Failed to fetch user storage:', error);
    return { totalStorage: 1, usedStorage: 0, availableStorage: 1 };
  }
};

/** Create Cashfree payment order (requires auth). Returns paymentSessionId for checkout. planData: { storage, period, price, planId }, returnUrl optional. */
export const createPaymentOrder = async (planData, returnUrl) => {
  const body = {
    storage: planData.storage,
    period: planData.period,
    price: planData.price,
  };
  if (planData.planId != null) body.planId = planData.planId;
  if (planData.isRenew != null) body.isRenew = !!planData.isRenew;
  if (returnUrl) body.returnUrl = returnUrl;
  const res = await axios.post('/storage/create-order', body);
  const data = res.data;
  if (data && data.success) {
    return {
      success: true,
      orderId: data.orderId,
      paymentSessionId: data.paymentSessionId,
      returnUrl: data.returnUrl,
      cashfreeMode: data.cashfreeMode,
    };
  }
  throw new Error(data?.message || 'Failed to create order');
};

/** Confirm payment success and fulfill storage (requires auth). Call after Cashfree redirect. */
export const confirmPaymentSuccess = async (orderId) => {
  const res = await axios.post('/storage/payment-success', { order_id: orderId });
  const data = res.data;
  if (data && data.success) {
    return { success: true, message: data.message || 'Storage purchased successfully', storage: data.storage };
  }
  throw new Error(data?.message || 'Payment confirmation failed');
};

/** Purchase storage directly without gateway (requires auth). planData: { storage, period, price, planId } */
export const purchaseStorage = async (planData, userId) => {
  try {
    const body = {
      storage: planData.storage,
      period: planData.period,
      price: planData.price,
    };
    if (planData.planId != null) body.planId = planData.planId;
    const res = await axios.post('/storage/purchase', body);
    const data = res.data;
    if (data && data.success) {
      return { success: true, message: data.message || 'Storage purchased successfully', storage: data.storage };
    }
    throw new Error(data?.message || 'Purchase failed');
  } catch (error) {
    const msg = error.response?.data?.message || error.message || 'Purchase failed';
    throw new Error(msg);
  }
};

/** No longer used for customer – backend updates storage on upload/delete */
export const updateStorageUsage = async (userId, sizeInGB) => {
  return Promise.resolve();
};

export const updateStoragePlan = async (planData) => {
  return Promise.resolve({ success: true });
};
